/**
 * File: apps/qbench-cp/reactapp/src/shared/utils/fields.js
 *
 * Purpose: Field-configuration lookup, async select-option loaders, entity field sorting/
 *     display rendering, and entity-relationship metadata for the customer portal's dynamic
 *     forms and tables.
 *
 * Scope: Additional-field configuration, react-select async loaders for relationship fields
 *     (turnaround, project, source, customer, contact, user, etc.), and field-driven display
 *     rendering for orders/samples/tests.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: `getFieldConfig` reads cached field configuration out of sessionStorage per data
 *     type/attribute. A family of `*Select` objects (TurnaroundSelect, ProjectSelect,
 *     AccessioningTypeSelect, SourceSelect, CustomerSelect, ContactSelect, UserSelect,
 *     TestStatuses) each provide `loadOptions`/`getOptionLabel`/`getOptionValue`/
 *     `getDefaultValue` for react-select-async-paginate, backed by the shared
 *     `asyncLoadOptions` pager. `Meta` maps each `EntityType` to its relationship-field
 *     select configs and its field-config getter. `parseFieldConfig` normalizes a raw field
 *     config payload — sorting fields by portal order, filtering hidden fields, and special-
 *     casing panel and portal-view report config. `sortEntityBy`/`renderDisplayField`/
 *     `testResultsRender` handle sorting table rows by field type and rendering a field's
 *     display value (including object-relation, tag, hyperlink, and worksheet-driven test
 *     result values). `entityRefMap` provides a second, order/sample/test-scoped map of
 *     relationship-field option accessors, some marked "keep for backward compatibility
 *     until V1 is deprecated."
 *
 * Dependencies: sessionStorage key "fieldConfigs" populated elsewhere in the app; shared/
 *     configs/rbac/accessPolicies for creatable-relationship policies
 *
 * Exports: getFieldConfig, convertDateFromServer, AddlFieldSelect, TurnaroundSelect,
 *     ProjectSelect, AccessioningTypeSelect, SourceSelect, CustomerSelect, ContactSelect,
 *     UserSelect, TestStatuses, Meta, parseFieldConfig, sortEntityBy, cutIgnoredItems,
 *     renderDisplayField, testResultsRender, CreateOrderCommonContentToString, entityRefMap
 */

import { useAppStore } from "App/store";
import { useOrderRequest } from "context/orderRequest";
import pick from 'lodash/pick';
import { DateTime } from "luxon";
import policies from "shared/configs/rbac/accessPolicies";
import { EntityType } from "shared/constants/entityTypes";
import { AddlFieldType, ColumnSort, PORTALVIEW_PERMISSION } from "shared/constants/fields";
import { useProjectCreate } from "shared/hooks/api/projects";
import { useSourceUpsert } from "shared/hooks/api/sources";
import styled from "styled-components";
import { axiosinstance } from "./api";
import { parseObjRelDefault } from "./form";
import { isNotEmpty, safeJsonParse } from "./functions";

export const getFieldConfig = (dataType, attribute = null) => {
  const res = safeJsonParse(sessionStorage.getItem('fieldConfigs'));
  if (isNotEmpty(res)) {
    const fieldConfig = res[dataType];
    return attribute 
      ? fieldConfig.find((item) => item.name === attribute)
      : fieldConfig
  }
  return;
};

export const convertDateFromServer = (date) => {
  return DateTime.fromFormat(date, "MM/dd/yyyy hh:mm a", { zone: "UTC" })
    .toLocal()
    .toFormat("MM/dd/yyyy hh:mm a");
};

/* ~~~~START Async Select ~~~~*/
const asyncLoadOptions = async (url, search, prevOptions, page) => {
  try {
    const delimiter = url.includes('?') ? '&' : '?';
    const queryString = `search=${search}&page=${page}`;
    const { data, total_pages, page_number } = await axiosinstance
      .get(`${url}${delimiter}${queryString}`)
      .then((res) => res.data);
    return {
      options: data,
      hasMore: page_number < total_pages,
      additional: {
        page: page + 1,
      },
    };
  } catch (err) {
    console.error(err);
  }
};
export const AddlFieldSelect = (options) => {
  const loadOptions = async (search, prevOptions, { page }) => {
    let filteredOptions;
    if (!search) {
      filteredOptions = options;
    } else {
      const searchLower = search.toLowerCase();

      filteredOptions = options.filter(({ label }) =>
        label.toLowerCase().includes(searchLower)
      );
    }

    const hasMore = filteredOptions.length > prevOptions.length + 10;
    const slicedOptions = filteredOptions.slice(
      prevOptions.length,
      prevOptions.length + 10
    );

    return {
      options: slicedOptions,
      hasMore,
    };
  };

  const getOptionLabel = (obj) => obj.label;
  const getOptionValue = (obj) => obj.value;
  const getDefaultValue = (obj) => {
    if (obj) {
      if (typeof obj === "string") {
        return { label: obj, value: obj };
      } else if (Array.isArray(obj)) {
        return obj.map((e) => ({ label: e, value: e }));
      }
    }
    return null;
  };

  return { loadOptions, getOptionLabel, getOptionValue, getDefaultValue };
};
export const TurnaroundSelect = () => {
  const {
    wip: { assayToAdd: assayId },
  } = useOrderRequest();
  const {
    appState: { filterTATByAssayEnabled },
  } = useAppStore();
  const url = filterTATByAssayEnabled && assayId
    ? `/v1/turnarounds?assay_ids=${assayId}`
    : '/v1/turnarounds';
  return {
    loadOptions: async (search, prevOptions, { page }) => 
    {

      const options = await asyncLoadOptions(url, search, prevOptions, page)
      if(page == 1){
        options.options.unshift({id: null, name: 'Standard'})
      }

      return options

    },
    getOptionLabel: (obj) => obj.name,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: (obj) => (obj ? { id: obj.id, name: obj.name } : { id: null, name: 'Standard' }),
  };
};
export const ProjectSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions("/v1/projects", search, prevOptions, page),
  getOptionLabel: (obj) => obj.title,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) => (obj ? { id: obj.id, title: obj.value } : null),
};
export const AccessioningTypeSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions("/v1/accessioningtypes", search, prevOptions, page),
  getOptionLabel: (obj) => obj.value,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) => (obj ? { id: obj.id, value: obj.value } : null),
};
export const SourceSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions("/v1/sources", search, prevOptions, page),
  getOptionLabel: (obj) => obj.display_name,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) =>
    obj ? { id: obj.id, value: obj.display_name } : null,
};
export const CustomerSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions("/v1/customers", search, prevOptions, page),
  getOptionLabel: (obj) => {
    let customer_name = obj?.customer_account?._display_fields_dict?.plain_text_value;
    return customer_name || obj.customer_name},
  getOptionValue: (obj) => {
    return obj.id || obj.customer_account_id},
  getDefaultValue: (obj) => {
    if (Array.isArray(obj)) {
      let val = [];
      for (let i = 0; i < obj.length; i++) {
        val.push({ id: obj.id, value: obj.customer_name });
      }
      return val;
    } else if (obj) {
      return { id: obj.id, value: obj.customer_name };
    }
    return null;
  },
};

export const ContactSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions("/v1/contacts", search, prevOptions, page),
  getOptionLabel: (obj) => obj._display_fields_dict?.plain_text_value,
  getOptionValue: (obj) => obj.id,
  getDefaultValue: (obj) => {
    if (Array.isArray(obj)) {
      let val = [];
      for (let i = 0; i < obj.length; i++) {
        val.push({
          id: obj.id,
          value: obj._display_fields_dict?.plain_text_value,
        });
      }
      return val;
    } else if (obj) {
      return { id: obj.id, value: obj._display_fields_dict?.plain_text_value };
    }
    return null;
  },
};

export const UserSelect = {
  loadOptions: async (search, prevOptions, { page }) =>
    asyncLoadOptions('/v1/users', search, prevOptions, page),
  getOptionLabel: (obj) =>
    obj?._display_fields_dict?.plain_text_value || obj?.value || obj?.label,
  getOptionValue: (obj) => obj?.id || obj?.value,
  getDefaultValue: (obj) => {
    if (obj) {
      if (obj?.id) {
        return {
          id: obj.id,
          value: obj._display_fields_dict?.plain_text_value || obj.value,
        };
      } else if (obj?.label) {
        return { id: obj.value, value: obj?.label };
      }
    }
    return null;
  },
};

export const TestStatuses = () => {
  const { appState } = useAppStore();
  const { name: _, ...testStatuses } = appState.fieldConfigs?.STATUSES.find(
    (s) => s.name === EntityType.TEST
  );
  const subStatusIndent = Array(4).join('\u00A0');
  const options = Object.entries(testStatuses)
    .map(([name, values]) => ({ name, ...values }))
    .sort((a, b) => a.sort_order - b.sort_order)
    .reduce((statuses, status) => {
      const subStatuses = status.children.map((c) => ({
        value: c.name,
        label: `${subStatusIndent}${c.name}`,
      }));
      return [
        ...statuses, 
        { value: status.name, label: status.name },
        ...subStatuses
      ];
    }, []);
  return {
    loadOptions: async () => ({ options }),
    getOptionLabel: (obj) => obj.label,
    getOptionValue: (obj) => obj.value,
    getDefaultValue: (obj) => (obj ? { value: obj.value, label: obj.label } : null),
  };
};

/* ~~~~END Async Select ~~~~*/

export const Meta = {
  [EntityType.ORDER]: {
    relationshipFields: {
      turnaround: TurnaroundSelect,
      project: ProjectSelect,
      additional_customers: CustomerSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.ORDER),
  },
  [EntityType.SAMPLE]: {
    relationshipFields: {
      accessioning_type: AccessioningTypeSelect,
      project: ProjectSelect,
      source: SourceSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.SAMPLE),
  },
  [EntityType.TEST]: {
    relationshipFields: {
      assay: {}, // TODO
      sample: {}, // TODO
      turnaround: TurnaroundSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.TEST),
  },
  [EntityType.SOURCE]: {
    relationshipFields: {
      project: ProjectSelect,
    },
    getFieldConfig: () => getFieldConfig(EntityType.SOURCE),
  },
  [EntityType.CUSTOMER]: {
    relationshipFields: {
    },
    getFieldConfig: () => getFieldConfig(EntityType.CUSTOMER),
  },
  [EntityType.CONTACT]: {
    relationshipFields: {},
    getFieldConfig: () => getFieldConfig(EntityType.CONTACT),
  },
  [EntityType.PROJECT]: {
    relationshipFields: {},
    getFieldConfig: () => getFieldConfig(EntityType.PROJECT),
  },
};

export const parseFieldConfig = (data) => {
  let fields = {};

  function _parsePanelFieldConfig(config) {
    const { additional_attribute: isAdditional } = config; 
    if (!isAdditional) return config;
    return ({
      ...config,
      portal_hidden: false,
      portal_global_hidden: false,
    });
  }

  function _parseReportPortalView(portalViewConfig = {}) {
    const { REPORT: reportConfig } = portalViewConfig;
    const pickList = [
      PORTALVIEW_PERMISSION.KEEP_OVERRIDDEN_REPORTS,
      PORTALVIEW_PERMISSION.INDICATE_OVERRIDDEN_REPORTS,
    ];
    return {
      ...portalViewConfig,
      ...pick(reportConfig, pickList),
    };
  }

  for (let key in data) {
    let config = data[key];

    if (
      ["ENABLED_MODULES", "ENTITY_DISPLAY_NAMES", "PORTAL_VIEW", "SETTINGS"].includes(key)
    ) {
      fields[key] =
        key === 'PORTAL_VIEW' ? _parseReportPortalView(config) : config;
      continue;
    }

    if (config) {
      const isPanel = key === EntityType.PANEL;
      fields[key] = Object.keys(config)
        .sort((x, y) => {
          let first = parseInt(config[x][isPanel ? 'order' : 'portal_order']),
            second = parseInt(config[y][isPanel ? 'order' : 'portal_order']);
          if (first === second) {
            return 0;
          } else if (isNaN(first)) {
            return 1;
          } else if (isNaN(second)) {
            return -1;
          } else {
            return first > second ? 1 : -1;
          }
        })
        .map((key) => {
          return { name: key, ...config[key] };
        })
        .map(config => isPanel ? _parsePanelFieldConfig(config) : config)
        .filter(
          (config) =>
            !(
              config.global_hidden ||
              config.hidden ||
              config.portal_global_hidden ||
              config.portal_hidden
            )
        );
    }
  }
  return fields;
};

export const sortEntityBy = (
  data,
  sortProp,
  direction,
  dataType = AddlFieldType.TEXT
) => {
  try {
    switch (dataType) {
      case AddlFieldType.TEXT:
        return sortByText(data, sortProp, direction);
      case AddlFieldType.DATETIME:
        return sortByDateTime(data, sortProp, direction);
      case AddlFieldType.NUM:
        return sortByNumber(data, sortProp, direction);
      default:
        return data;
    }
  } catch (err) {
    return data;
  }
};

const sortByText = (data, sortProp, direction) => {
  if (direction === ColumnSort.ASC) {
    return data.sort(function (a, b) {
      return a[sortProp].localeCompare(b[sortProp]);
    });
  }
  if (direction === ColumnSort.DESC) {
    return data.sort(function (a, b) {
      return b[sortProp].localeCompare(a[sortProp]);
    });
  }
  return data;
};

const sortByDateTime = (data, sortProp, direction) => {
  if (direction === ColumnSort.ASC) {
    return data.sort((a, b) => {
      return new Date(a[sortProp]) - new Date(b[sortProp]);
    });
  }
  if (direction === ColumnSort.DESC) {
    return data.sort((a, b) => {
      return new Date(b[sortProp]) - new Date(a[sortProp]);
    });
  }
  return data;
};

const sortByNumber = (data, sortProp, direction) => {
  return data.sort((a, b) => {
    if (direction === ColumnSort.ASC) {
      return a[sortProp] - b[sortProp]; // Ascending
    } else if (direction === ColumnSort.DESC) {
      return b[sortProp] - a[sortProp]; // Descending
    }
    return 0; // No sorting if direction is not recognized
  });
};

export const cutIgnoredItems = (
  list,
  config = [],
  numberOfColumns = undefined
) => {
  return list
    .filter((item) => {
      return !config.some((c) => item[c.field] === c.rule);
    })
    .filter((_, idx) => (numberOfColumns ? idx < numberOfColumns : true));
};

const StyledTabAnchorTag = styled.a`
  &:hover {
    text-decoration: none;
  }
`;

export const renderDisplayField = (entity, fieldConfig, options = {}) => {
  const { as: Wrapper } = options;

  let value = entity[fieldConfig.name];
  if (fieldConfig.name === "status") {
    value = entity.status ?? entity.state;
  }
  if (fieldConfig.field_type === AddlFieldType.BOOL) {
    value = value === true ? "YES" : "NO";
  } else if (!value || (Array.isArray(value) && !value.length)) return "-";

  if (fieldConfig.field_type === AddlFieldType.HYPERLINK) {
    value = (
      <StyledTabAnchorTag href={value} target="_blank">
        {value}
      </StyledTabAnchorTag>
    );
  } else if (
    fieldConfig.field_type === AddlFieldType.TAG &&
    Array.isArray(value)
  ) {
    value = value.map((tag) => tag.value).join(", ");
  } else if (
    typeof value === "object" &&
    value.hasOwnProperty("_display_fields_dict")
  ) {
    value = value._display_fields_dict.plain_text_value;
  } else if (value && value.constructor === Array) {
    value = value
      .map((item) => item?._display_fields_dict?.plain_text_value ?? item)
      .join(", ");
  } else if (
    fieldConfig.field_type === AddlFieldType.OBJECT_RELATION ||
    fieldConfig.field_type === AddlFieldType.OBJECT_RELATION_MULTI
  ) {
    value = entity[`${fieldConfig.name}_to_display_text`];
  } else if (fieldConfig.field_type === AddlFieldType.DATETIME) {
    value = convertDateFromServer(value);
  }

  return Wrapper ? <Wrapper>{value}</Wrapper> : <strong>{value}</strong>;
};

export const testResultsRender = (value, row) => {
  const returnString = (row, data, showString) => {
    let dataType = data
      ? data.constructor === Array
        ? "array"
        : data.constructor === Object
        ? "obj"
        : data.constructor === String
        ? "string"
        : ""
      : "";
    switch (dataType) {
      case "string":
        return data;
      case "array":
        return data.length ? data.map((item) => item.value).join(", ") : "";
      case "obj":
        return data.value
          ? data.value
          : data._display_fields_dict
          ? data._display_fields_dict.plain_text_value
          : "";
      default:
        return row["showString"];
    }
  };

  if (row.release_results) {
    if (row.assay.cp_result_field && row.assay.cp_result_field_type) {
      switch (row.assay.cp_result_field_type) {
        case "TEST_FIELD":
          const fieldConfig = getFieldConfig("TEST", row.assay.cp_result_field);
          if (fieldConfig) {
            return renderDisplayField(row, fieldConfig);
          }

          return returnString(row, row?.[row.assay.cp_result_field], "results");

        case "WORKSHEET_FIELD": {
          if (
            row.worksheet_data &&
            row.worksheet_data.hasOwnProperty(row.assay.cp_result_field)
          ) {
            return returnString(
              row,
              row.worksheet_data?.[row.assay.cp_result_field]?.value,
              "results"
            );
          } else {
            return row.state;
          }
        }
        default:
          return row.results;
      }
    } else {
      const portalView = JSON.parse(
        localStorage.getItem("portalView")
      )?.test_results_field;

      switch (portalView?.cp_result_field_type) {
        case "TEST_FIELD":
          const fieldConfig = getFieldConfig(
            "TEST",
            portalView?.cp_result_field
          );
          if (fieldConfig) {
            return renderDisplayField(row, fieldConfig);
          }

          return returnString(
            row,
            row?.[
              portalView?.cp_result_field === "status"
                ? "state"
                : portalView?.cp_result_field
            ],
            "results"
          );
        case "WORKSHEET_FIELD": {
          if (
            row.worksheet_data &&
            row.worksheet_data.hasOwnProperty(portalView?.cp_result_field)
          ) {
            return returnString(
              row,
              row.worksheet_data?.[portalView?.cp_result_field]?.value,
              "results"
            );
          } else {
            return row.state;
          }
        }
        default:
          return row.results;
      }
    }
  }
  return "-";
};

export const CreateOrderCommonContentToString = (
  hidden,
  sampleFieldName,
  fieldName,
  sampleMeta,
  fieldConfig
) => {
  if (!hidden && sampleFieldName) {
    let val;
    if (Object.keys(sampleMeta.relationshipFields).includes(fieldName)) {
      val =
        sampleMeta.relationshipFields[fieldName].getOptionLabel(
          sampleFieldName
        );
    } else if (fieldConfig.field_type === "SELECT") {
      val = showSelectedValue(sampleFieldName);
    } else if (fieldConfig.field_type === "SELECT_MULTI") {
      val = sampleFieldName.map((ele) => showSelectedValue(ele)).join(",");
    } else if (fieldConfig.field_type === "DATETIME") {
      val = convertDateFromServer(sampleFieldName);
    } else {
      val = sampleFieldName;
    }
    return val;
  }
};

const showSelectedValue = (data) => {
  if (typeof data === "object") {
    return data.label;
  } else {
    return data;
  }
};

/**
 * Keep for backward compatibilty until V1 is deprecated.
 */
const entityRefCoreBase = {
  [EntityType.PROJECT]: {
    getOptionLabel: (obj) => obj.title,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: parseObjRelDefault,
    creatable: {
      mutation: useProjectCreate,
      policy: policies.ui.projectDropdownCreateBtn,
    },
  },
  [EntityType.TURNAROUND]: {
    getOptionLabel: (obj) => obj.name,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: (obj) =>
      parseObjRelDefault(obj, {
        value: null,
        label: 'Standard',
      }),
  },
};
const entityRefAddlBase = {
  [EntityType.USER]: {
    getOptionLabel: (obj) =>
      obj._display_fields_dict?.plain_text_value || obj.value || obj.label,
    getOptionValue: (obj) => obj.id || obj.value,
    getDefaultValue: parseObjRelDefault,
  },
  [EntityType.CONTACT]: {
    getOptionLabel: (obj) => obj._display_fields_dict?.plain_text_value,
    getOptionValue: (obj) => obj.id,
    getDefaultValue: parseObjRelDefault,
  },
};
export const entityRefMap = {
  [EntityType.ORDER]: {
    [EntityType.CUSTOMER]: {
      getOptionLabel: (obj) =>
        obj.customer_name || obj.customer_account?.customer_name,
      getOptionValue: (obj) => obj.id || obj.customer_account_id,
      getDefaultValue: parseObjRelDefault,
    },
    [EntityType.TURNAROUND]: entityRefCoreBase[EntityType.TURNAROUND],
    [EntityType.PROJECT]: entityRefCoreBase[EntityType.PROJECT],
    ...entityRefAddlBase,
  },
  [EntityType.SAMPLE]: {
    [EntityType.PROJECT]: entityRefCoreBase[EntityType.PROJECT],
    [EntityType.SOURCE]: {
      getOptionLabel: (obj) => obj.display_name,
      getOptionValue: (obj) => obj.id,
      getDefaultValue: parseObjRelDefault,
      creatable: {
        mutation: useSourceUpsert,
      },
    },
    [EntityType.ACCESSIONING_TYPE]: {
      getOptionLabel: (obj) => obj.value,
      getOptionValue: (obj) => obj.id,
      getDefaultValue: parseObjRelDefault,
    },
    ...entityRefAddlBase,
  },
  [EntityType.TEST]: {
    [EntityType.TURNAROUND]: entityRefCoreBase[EntityType.TURNAROUND],
    ...entityRefAddlBase,
  },
  [EntityType.SOURCE]: {
    [EntityType.PROJECT]: entityRefCoreBase[EntityType.PROJECT],
    ...entityRefAddlBase,
  },
  [EntityType.CONTACT]: entityRefAddlBase,
  [EntityType.CUSTOMER]: entityRefAddlBase,
};
